import { Currency, CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import { computePairAddress, Pair } from '@kodiak-finance/v2-sdk'
import { V2_FACTORY_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import JSBI from 'jsbi'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useV2PositionByPair } from 'pages/Liquidity/ProtocolLists/v2/hooks/useUserV2Positions'
import { ReactNode, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { REFETCH_POLICY_FAST } from '../../config/RefetchPolicies'
import { useTokenBalances } from '../connection/hooks'
import { AppState } from '../index'
import { Field, typeInput } from './actions'

export function useBurnState(): AppState['burn'] {
  return useAppSelector((state) => state.burn)
}

export function useDerivedBurnInfo(
  currencyA: Currency | undefined,
  currencyB: Currency | undefined
): {
  pair?: Pair | null
  parsedAmounts: {
    [Field.LIQUIDITY_PERCENT]: Percent
    [Field.LIQUIDITY]?: CurrencyAmount<Token>
    [Field.CURRENCY_A]?: CurrencyAmount<Currency>
    [Field.CURRENCY_B]?: CurrencyAmount<Currency>
  }
  totalSupply: CurrencyAmount<Token> | undefined
  liquidityTokenBalance: CurrencyAmount<Token> | undefined
  positionValueUSD: number | undefined
  error?: ReactNode
} {
  const { account, chainId } = useConnection()

  const { independentField, typedValue } = useBurnState()

  const pairAddress = useMemo(() => {
    if (!currencyA || !currencyB || !chainId) return undefined

    const tokenA = currencyA.wrapped
    const tokenB = currencyB.wrapped
    const factoryAddress = V2_FACTORY_ADDRESSES[chainId]

    if (!tokenA || !tokenB || !factoryAddress) return undefined

    try {
      return computePairAddress({ factoryAddress, tokenA, tokenB }).toLowerCase()
    } catch {
      return undefined
    }
  }, [currencyA, currencyB, chainId])

  // pair + totalsupply
  const { position } = useV2PositionByPair(account, pairAddress, chainId)
  const pair = position?.pair || null
  const liquidityTokenBalance = position?.liquidityTokenBalance
  const totalSupply = position?.totalSupply
  const valueUSD = position?.valueUSD

  // balances
  const relevantTokenBalances = useTokenBalances(account ?? undefined, [pair?.liquidityToken], REFETCH_POLICY_FAST)
  const userLiquidity: undefined | CurrencyAmount<Token> = relevantTokenBalances?.[pair?.liquidityToken?.address ?? '']

  const [tokenA, tokenB] = [currencyA?.wrapped, currencyB?.wrapped]
  const tokens = {
    [Field.CURRENCY_A]: tokenA,
    [Field.CURRENCY_B]: tokenB,
    [Field.LIQUIDITY]: pair?.liquidityToken,
  }

  // liquidity values
  // const totalSupply = useTotalSupply(pair?.liquidityToken)
  const isTotalSupplyZero = totalSupply ? totalSupply.equalTo(0) : false

  let liquidityValueA: CurrencyAmount<Token> | undefined = undefined
  let liquidityValueB: CurrencyAmount<Token> | undefined = undefined

  if (
    pair &&
    totalSupply &&
    userLiquidity &&
    tokenA &&
    !isTotalSupplyZero &&
    JSBI.greaterThanOrEqual(totalSupply.quotient, userLiquidity.quotient)
  ) {
    try {
      liquidityValueA = CurrencyAmount.fromRawAmount(
        tokenA,
        pair.getLiquidityValue(tokenA, totalSupply, userLiquidity, false).quotient
      )
    } catch (e) {
      console.error('Error calculating liquidityValueA:', e)
    }
  }

  if (
    pair &&
    totalSupply &&
    userLiquidity &&
    tokenB &&
    !isTotalSupplyZero &&
    JSBI.greaterThanOrEqual(totalSupply.quotient, userLiquidity.quotient)
  ) {
    try {
      liquidityValueB = CurrencyAmount.fromRawAmount(
        tokenB,
        pair.getLiquidityValue(tokenB, totalSupply, userLiquidity, false).quotient
      )
    } catch (e) {
      console.error('Error calculating liquidityValueB:', e)
    }
  }

  const liquidityValues: { [Field.CURRENCY_A]?: CurrencyAmount<Token>; [Field.CURRENCY_B]?: CurrencyAmount<Token> } = {
    [Field.CURRENCY_A]: liquidityValueA,
    [Field.CURRENCY_B]: liquidityValueB,
  }

  let percentToRemove: Percent = new Percent('0', '100')
  // user specified a %
  if (independentField === Field.LIQUIDITY_PERCENT) {
    percentToRemove = new Percent(typedValue, '100')
  }

  // user specified a specific amount of liquidity tokens
  else if (independentField === Field.LIQUIDITY) {
    if (pair?.liquidityToken) {
      const independentAmount = tryParseCurrencyAmount(typedValue, pair.liquidityToken)
      if (independentAmount && userLiquidity && !independentAmount.greaterThan(userLiquidity)) {
        percentToRemove = new Percent(independentAmount.quotient, userLiquidity.quotient)
      }
    }
  }
  // user specified a specific amount of token a or b
  else {
    if (tokens[independentField]) {
      const independentAmount = tryParseCurrencyAmount(typedValue, tokens[independentField])
      const liquidityValue = liquidityValues[independentField]
      if (independentAmount && liquidityValue && !independentAmount.greaterThan(liquidityValue)) {
        percentToRemove = new Percent(independentAmount.quotient, liquidityValue.quotient)
      }
    }
  }

  const parsedAmounts: {
    [Field.LIQUIDITY_PERCENT]: Percent
    [Field.LIQUIDITY]?: CurrencyAmount<Token>
    [Field.CURRENCY_A]?: CurrencyAmount<Currency>
    [Field.CURRENCY_B]?: CurrencyAmount<Currency>
  } = {
    [Field.LIQUIDITY_PERCENT]: percentToRemove,
    [Field.LIQUIDITY]:
      userLiquidity && percentToRemove && percentToRemove.greaterThan('0')
        ? CurrencyAmount.fromRawAmount(
            userLiquidity.currency,
            percentToRemove.multiply(userLiquidity.quotient).quotient
          )
        : undefined,
    [Field.CURRENCY_A]:
      tokenA && percentToRemove && percentToRemove.greaterThan('0') && liquidityValueA
        ? CurrencyAmount.fromRawAmount(tokenA, percentToRemove.multiply(liquidityValueA.quotient).quotient)
        : undefined,
    [Field.CURRENCY_B]:
      tokenB && percentToRemove && percentToRemove.greaterThan('0') && liquidityValueB
        ? CurrencyAmount.fromRawAmount(tokenB, percentToRemove.multiply(liquidityValueB.quotient).quotient)
        : undefined,
  }

  let error: ReactNode | undefined
  if (!account) {
    error = <>Connect Wallet</>
  }

  if (!parsedAmounts[Field.LIQUIDITY] || !parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]) {
    error = error ?? <>Enter an amount</>
  }

  return { pair, parsedAmounts, totalSupply, liquidityTokenBalance, positionValueUSD: valueUSD, error }
}

export function useBurnActionHandlers(): {
  onUserInput: (field: Field, typedValue: string) => void
} {
  const dispatch = useAppDispatch()

  const onUserInput = useCallback(
    (field: Field, typedValue: string) => {
      dispatch(typeInput({ field, typedValue }))
    },
    [dispatch]
  )

  return {
    onUserInput,
  }
}
